/* eslint-disable max-len */
import { Dispatch } from "redux"
import { ValidationError } from "sequelize"
import {
    CcReviewComplianceRevision,
    CcReviewComplianceRevisionSave,
    CcReviewComplianceItemState,
    CcReviewComplianceHistoryCreate,
    CcReviewComplianceHistory,
    CcReviewComplianceHistoryUpdateResponse,
    CcReviewComplianceHistoryImportProps,
    CcReviewComplianceHistoryVersion,
    CcReviewComplianceTempAnswer,
    CcReviewComplianceTempAnswerVersion,
} from "server/models"

import { GET, POST, DELETE, validate } from "client/store/utils"
import { LangType } from "client/store"
import {
    ReviewComplianceRevisionValidation,
    ReviewComplianceHistoryValidation,
    ReviewComplianceTempAnswerValidation,

    ReviewComplianceStateList,
    ReviewComplianceHistoryList,
    ReviewComplianceTempAnswerList,
    ReviewComplianceTempAnswerByUser,
    ReviewComplianceTempAnswerVersionByUser,
} from "./types"
import {
    setCcReviewComplianceItemStateList,
    setCcReviewComplianceItemHistoryList,
    setCcReviewComplianceTempAnswerList,
} from "./actions"

// review compliance revisions

export const LoadCcReviewComplianceRevisionData = (revision: CcReviewComplianceRevision, dispatch: Dispatch) => (
    Promise.all([
        LoadAllCcReviewComplianceItemState(revision.id).then((res) => {
            dispatch(setCcReviewComplianceItemStateList(revision.setId, res))
        }),
        LoadAllCcReviewComplianceHistory(revision.id).then((res) => {
            dispatch(setCcReviewComplianceItemHistoryList(revision.setId, res))
        }),
        LoadCcReviewComplianceTempAnswerList(revision.id).then((res) => {
            dispatch(setCcReviewComplianceTempAnswerList(revision.setId, res))
        }),
    ])
)
export const LoadCcReviewComplianceRevisions = (projectId: number, fileId: number, versionId: number, setId: number) => (
    GET(`/ccReviewComplianceRevision/projectId/${projectId}/fileId/${fileId}/versionId/${versionId}/setId/${setId}`)
    .then((revisions: CcReviewComplianceRevision[]) => revisions)
)
export const CreateCcReviewComplianceRevision = (projectId: number, fileId: number, versionId: number, setId: number) => (
    GET(`/ccReviewComplianceRevision/create/projectId/${projectId}/fileId/${fileId}/versionId/${versionId}/setId/${setId}`)
    .then((revision: CcReviewComplianceRevision) => revision)
)
export const UpdateCcReviewComplianceRevision = (id: number, name: string, lang: LangType) => {
    const data: CcReviewComplianceRevisionSave = { id, name }
    return POST(
        "/ccReviewComplianceRevision/update",
        JSON.stringify(data),
    ).then((res) => {
        const error = res as ValidationError
        const validation = <ReviewComplianceRevisionValidation>validate(error, lang)

        return validation
    })
}
export const DeleteCcReviewComplianceRevision = (revisionId: number) => (
    DELETE(`/ccReviewComplianceRevision/delete/${revisionId}`)
    .then((removedRows: number) => removedRows)
)
export const DeleteAllCcReviewComplianceRevisions = (setId: number) => (
    DELETE(`/ccReviewComplianceRevision/deleteAll/${setId}`)
    .then((removedRows: number) => removedRows)
)

// review compliance state

export const LoadAllCcReviewComplianceItemState = (revisionId: number) => (
    GET(`/ccReviewComplianceItemState/listView/revision/${revisionId}`)
    .then((stateArray: CcReviewComplianceItemState[]) => {
        const stateList: ReviewComplianceStateList = {}
        stateArray.forEach((state) => {
            stateList[state.itemId] = state
        })
        return stateList
    })
)
export const LoadCcReviewComplianceItemState = (itemId: number, revisionId: number) => (
    GET(`/ccReviewComplianceItemState/item/${itemId}/revision/${revisionId}`)
    .then((state: CcReviewComplianceItemState) => state)
)
export const UpdateCcReviewComplianceVerifier = (data: CcReviewComplianceItemState[]) => (
    POST(
        "/ccReviewComplianceItemState/verifier",
        JSON.stringify(data),
    )
)
export const UpdateCcReviewComplianceState = (data: CcReviewComplianceItemState) => (
    POST(
        "/ccReviewComplianceItemState/update",
        JSON.stringify(data),
    )
)
export const CreateCcReviewComplianceState = (data: CcReviewComplianceItemState) => (
    POST(
        "/ccReviewComplianceItemState/create",
        JSON.stringify(data),
    )
)

// review compliance history

export const CreateManyCcReviewComplianceHistory = (
    historyArray: CcReviewComplianceHistoryCreate[],
) => (
    POST(
        "/ccReviewComplianceHistory/bulkCreate",
        JSON.stringify(historyArray),
    )
)
export const CreateCcReviewComplianceHistory = (
    history: CcReviewComplianceHistoryCreate,
    lang: LangType,
) => (
    POST(
        "/ccReviewComplianceHistory/create",
        JSON.stringify(history),
    ).then((res) => {
        const newHistory = res as CcReviewComplianceHistory
        const error = res as ValidationError
        const validation = <ReviewComplianceHistoryValidation>validate(error, lang)

        return {
            newHistoryId: newHistory.id || null,
            validation,
        }
    })
)
export const CreateCcReviewComplianceHistoryVersions = (versions: CcReviewComplianceHistoryVersion[]) => (
    POST(
        "/ccReviewComplianceHistoryVersion/save",
        JSON.stringify(versions),
    )
)
export const UpdateCcReviewComplianceHistoryResponse = ( // for Super Admin only
    id: number,
    responseId: number,
    answer: string,
    lang: LangType,
) => {
    const data: CcReviewComplianceHistoryUpdateResponse = {
        id,
        responseId,
        answer,
    }
    return POST(
        "/ccReviewComplianceHistory/updateResponse",
        JSON.stringify(data),
    ).then((res) => {
        const error = res as ValidationError
        const validation = <ReviewComplianceHistoryValidation>validate(error, lang)
        return validation
    })
}
export const LoadCcReviewComplianceHistory = (itemId: number, revisionId: number) => (
    GET(`/ccReviewComplianceHistory/item/${itemId}/revision/${revisionId}`)
    .then((history: CcReviewComplianceHistory[]) => history)
)
export const LoadAllCcReviewComplianceHistory = (revisionId: number) => (
    GET(`/ccReviewComplianceHistory/revision/${revisionId}`)
    .then((historyArray: CcReviewComplianceHistory[]) => {
        const historyList: ReviewComplianceHistoryList = {}
        historyArray.forEach((history) => {
            if (!historyList[history.itemId]) historyList[history.itemId] = []
            historyList[history.itemId].push(history)
        })
        return historyList
    })
)
export const ImportCcReviewComplianceHistory = (data: CcReviewComplianceHistoryImportProps) => (
    POST(
        "/ccReviewComplianceHistory/import",
        JSON.stringify(data),
    )
)

// temp answer

export const LoadCcReviewComplianceTempAnswerList = (revisionId: number) => (
    GET(`/ccReviewComplianceTempAnswer/revision/${revisionId}`)
    .then((answers: CcReviewComplianceTempAnswer[]) => {
        const answerList: ReviewComplianceTempAnswerList = {}
        answers.forEach((answer) => {
            if (!answerList[answer.itemId]) answerList[answer.itemId] = {}
            answerList[answer.itemId][answer.editorId] = answer
        })
        return answerList
    })
)
export const LoadCcReviewComplianceTempAnswers = (itemId: number, revisionId: number) => (
    GET(`/ccReviewComplianceTempAnswer/item/${itemId}/revision/${revisionId}`)
    .then((answers: CcReviewComplianceTempAnswer[]) => {
        const tempAnswers: ReviewComplianceTempAnswerByUser = {}
        answers.forEach((answer) => {
            tempAnswers[answer.editorId] = answer
        })
        return tempAnswers
    })
)
export const SaveCcReviewComplianceTempAnswer = (
    answer: CcReviewComplianceTempAnswer,
    lang: LangType,
) => (
    POST(
        "/ccReviewComplianceTempAnswer/save",
        JSON.stringify(answer),
    ).then((res) => {
        const error = res as ValidationError
        const validation = <ReviewComplianceTempAnswerValidation>validate(error, lang)
        return validation
    })
)
export const DeleteCcReviewComplianceTempAnswers = (itemId: number, revisionId: number) => (
    DELETE(`/ccReviewComplianceTempAnswer/delete/item/${itemId}/revision/${revisionId}`)
    .then((removedRows: number) => removedRows)
)

// temp answer versions

export const LoadCcReviewComplianceTempAnswerVersions = (itemId: number, revisionId: number) => (
    GET(`/ccReviewComplianceTempAnswerVersion/item/${itemId}/revision/${revisionId}`)
    .then((versions: CcReviewComplianceTempAnswerVersion[]) => {
        const tempVersions: ReviewComplianceTempAnswerVersionByUser = {}
        versions.forEach((version) => {
            if (!tempVersions[version.editorId]) tempVersions[version.editorId] = []
            tempVersions[version.editorId].push(version)
        })
        return tempVersions
    })
)
export const SaveCcReviewComplianceTempAnswerVersions = (versions: CcReviewComplianceTempAnswerVersion[]) => (
    POST(
        "/ccReviewComplianceTempAnswerVersion/save",
        JSON.stringify(versions),
    )
)
export const DeleteCcReviewComplianceTempAnswerVersions = (itemId: number, revisionId: number, editorId: number) => (
    DELETE(`/ccReviewComplianceTempAnswerVersion/delete/item/${itemId}/revision/${revisionId}/editor/${editorId}`)
    .then((removedRows: number) => removedRows)
)
